import { CommonModule } from '@angular/common';
import { Component, inject } from '@angular/core';
import { NgbModal, NgbModalOptions } from '@ng-bootstrap/ng-bootstrap';
import { FilterService } from '../../../../../_shared/services';
import { BaseWidgetComponent } from '../../../../../home/home-widgets/widgets/base-widget.component';
import { ExecutiveStatsRequest, ExecutiveStatsSummaryRequest } from '../../../../../_shared/models/driver-summary-request.model';
import { ExecutiveStatsService } from '../../../services/executive-stats.service';
import { ErrorHandlerService } from '../../../../../_shared/services/error-handler.service';
import { GraphTrendIconComponent } from '../../../../../_shared/components/graphs/graph-trend-icon/graph-trend-icon.component';
import { MultiLineChartComponent, MultiLineChartDataPoint, MultiLineChartSeries, LegendItem } from '../../../../../_shared/components/graphs/multi-line-chart/multi-line-chart.component';
import { EmptyStateComponent } from '../../../../../_shared/components/states/empty-state/empty-state.component';
import { TimeSeriesGroupingHelper } from '../../../../../_shared/helpers/chart-grouping/time-series-grouping.helper';
import { differenceInDays } from 'date-fns';
import { DistanceDrivenModalComponent } from '../../modals/distance-driven-modal/distance-driven-modal.component';
import { GraphErrorStateComponent } from '../../../../../_shared/components/graphs/graph-error-state/graph-error-state.component';
import { formatValue } from '../../../../../_shared/helpers/kpiWidget/kpi-formatters.helper';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';

@Component({
  selector: 'app-distance-driven-widget',
  standalone: true,
  imports: [CommonModule, MultiLineChartComponent, GraphTrendIconComponent, EmptyStateComponent, GraphErrorStateComponent, TranslocoDirective],
  templateUrl: './distance-driven-widget.component.html',
  styleUrl: './distance-driven-widget.component.scss'
})
export class DistanceDrivenWidgetComponent extends BaseWidgetComponent {
  private modalService = inject(NgbModal);
  private executiveStatsService = inject(ExecutiveStatsService);
  private errorHandlerService = inject(ErrorHandlerService);
  private transloco = inject(TranslocoService);

  public hasError: boolean = false;
  public errorMessage: string = '';
  public hasResult: boolean = false;
  public chartData: MultiLineChartDataPoint[] = [];
  readonly chartHeight = 175;
  private readonly defaultYTicks = [100, 80, 60, 40, 20, 0];
  yTicks: number[] = this.defaultYTicks;

  readonly legendItems: LegendItem[] = [
    { legend: this.transloco.translate('executiveStats.chart.kmDrivenPerDay'), color: '#EE2737' },
    { legend: this.transloco.translate('executiveStats.chart.globalTrends'), color: '#3873C9' }
  ];
  readonly series: MultiLineChartSeries[] = [
    { key: 'KmDrivenPerDay', color: '#EE2737', label: this.transloco.translate('executiveStats.chart.kmDrivenPerDay'), showDots: false },
    { key: 'Globaltrends', color: '#3873C9', label: this.transloco.translate('executiveStats.chart.globalTrends'), showDots: false }
  ];

  constructor() {
    const filterService = inject(FilterService);
    super(filterService);
    this.isLoading = true;
  }

  protected override async onFilterChangeAsync(): Promise<void> {
    this.hasError = false;
    this.isLoading = true;

    try {
      const summaryRequest: ExecutiveStatsSummaryRequest = {
        driverIds: [...this.widgetFilter.driverIds],
        accountIds: [...this.widgetFilter.accountIds],
        timeFrom: this.widgetFilter.startDate!,
        timeTo: this.widgetFilter.endDate!,
        previousTimeFrom: this.widgetFilter.previousCycleStartDate!,
        previousTimeTo: this.widgetFilter.previousCycleEndDate!,
        interval: this.widgetFilter.interval
      };

      const request: ExecutiveStatsRequest = {
        driverIds: [...this.widgetFilter.driverIds],
        accountIds: [...this.widgetFilter.accountIds],
        timeFrom: this.widgetFilter.startDate!,
        timeTo: this.widgetFilter.endDate!,
        interval: this.widgetFilter.interval
      };

      this.cancelSubject.next();
      const [distanceDriven, distanceDrivenInsights, globalDistanceDriven] = await Promise.all([
        this.executiveStatsService.getDistanceDriven(request, this.cancelSubject),
        this.executiveStatsService.getDistanceDrivenInsightsSummary(summaryRequest, this.cancelSubject),
        this.executiveStatsService.getGlobalDistanceDriven(request, this.cancelSubject)
      ]);

      this.chartData = this.processData(
        distanceDriven.data || [],
        globalDistanceDriven.data || []
      );
      this.yTicks = this.calculateYTicks(this.chartData);

      this.value = distanceDrivenInsights.data?.totalValue ?? 0;
      this.percentageDifference = distanceDrivenInsights.data?.percentageDifference ?? 0;
      this.hasResult = this.chartData.some(point =>
        this.series.some(s => (Number(point[s.key]) || 0) > 0)
      );

      this.isLoading = false;
    } catch (error: any) {
      this.errorHandlerService.handleError(error, {
        callback: (message) => {
          this.hasError = true;
          this.errorMessage = message;
          this.isLoading = false;
        }
      });
    }
  }

  private processData(distanceData: any[], globalData: any[]): MultiLineChartDataPoint[] {
    const datasets = [
      { data: distanceData.map(x => ({ totalValue: x.totalValue, timeSeries: new Date(x.timeSeries) })), label: 'KmDrivenPerDay' },
      { data: globalData.map(x => ({ totalValue: x.totalValue, timeSeries: new Date(x.timeSeries) })), label: 'Globaltrends' }
    ];

    const daysDifference = differenceInDays(this.widgetFilter.endDate!, this.widgetFilter.startDate!);

    if (daysDifference === 0) {
      return TimeSeriesGroupingHelper.groupByHours(datasets);
    } else if (daysDifference <= 7) {
      return TimeSeriesGroupingHelper.groupByDays(datasets, this.widgetFilter.startDate!, this.widgetFilter.endDate!);
    } else if (daysDifference > 60) {
      return TimeSeriesGroupingHelper.groupByMonths(datasets, this.widgetFilter.startDate!, this.widgetFilter.endDate!);
    } else {
      return TimeSeriesGroupingHelper.groupByWeeks(datasets, this.widgetFilter.startDate!, this.widgetFilter.endDate!);
    }
  }

  private calculateYTicks(chartData: MultiLineChartDataPoint[]): number[] {
    if (!chartData?.length) return this.defaultYTicks;

    const allValues: number[] = [];
    chartData.forEach(point => {
      this.series.forEach(s => {
        const value = Number(point[s.key]) || 0;
        if (value > 0) allValues.push(value);
      });
    });

    if (!allValues.length) return this.defaultYTicks;

    const max = Math.max(...allValues);
    const roundedMax = Math.max(
      Math.ceil(max / 10) * 10,
      Math.ceil(max * 1.1 / 10) * 10
    );

    const tickInterval = roundedMax / 5;
    const ticks: number[] = [];
    for (let i = 0; i <= 5; i++) {
      ticks.push(Math.round(tickInterval * i));
    }

    return ticks.sort((a, b) => b - a);
  }

  formatYAxisValue = (value: number): string => {
    return formatValue(value, { unit: 'km' });
  }

  public override formatValue(value: number): string {
    return `${value.toLocaleString()} km`;
  }

  public formatDifference(value: number): string {
    return `${value.toLocaleString('default', { maximumFractionDigits: 2 })}%`;
  }

  formatPercentChange(): string {
    const sign = this.percentageDifference > 0 ? '+' : '';
    return `${sign}${this.percentageDifference.toLocaleString('default', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}%`;
  }

  formatTotalValue(): string {
    return `${this.value.toLocaleString()} km`;
  }

  isUptrendPositive(): boolean {
    return true;
  }

  tooltipFormat = (value: any): string => {
    return `${value.toLocaleString('default', { maximumFractionDigits: 2 })} km`;
  }

  hasData(): boolean {
    return this.hasResult && this.chartData.length > 0;
  }

  openModal(): void {
    const modalOptions: NgbModalOptions = {
      centered: true,
      backdrop: 'static',
      size: 'xl',
    };

    this.modalService.open(DistanceDrivenModalComponent, modalOptions);
  }
}